import { useState } from "react";
import { Copy, Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { BrutalInput } from "@/components/ui/BrutalInput";
import { BrutalButton } from "@/components/ui/BrutalButton";
import { toast } from "@/hooks/use-toast";

export interface CopyableUrlInputProps {
  // URL base without final slug
  baseUrl: string;
  
  // Current slug value
  value: string;
  
  // Callback when slug changes
  onChange: (value: string) => void;
  
  // ID for accessibility
  id?: string;
  
  // Label text
  label?: string;
  
  // Placeholder
  placeholder?: string;
  
  // Include https:// in copied URL
  includeProtocol?: boolean;
  
  // Function to validate/sanitize the slug
  sanitizeSlug?: (slug: string) => string;
  
  // Additional CSS classes
  className?: string;
}

export const CopyableUrlInput = ({
  baseUrl,
  value,
  onChange,
  id,
  label,
  placeholder,
  includeProtocol = true,
  sanitizeSlug,
  className,
}: CopyableUrlInputProps) => {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = async () => {
    const protocol = includeProtocol ? 'https://' : '';
    const fullUrl = `${protocol}${baseUrl}${value}`;
    
    try {
      await navigator.clipboard.writeText(fullUrl);
      setIsCopied(true);
      toast.success(t('components.copyableUrl.copiedSuccess'));
      
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast.error('Errore durante la copia');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    const sanitized = sanitizeSlug ? sanitizeSlug(newValue) : newValue;
    onChange(sanitized);
  };

  return (
    <div className={cn("space-y-2", className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <div className="flex items-stretch">
        {/* URL Prefix */}
        <span className="inline-flex items-center px-3 bg-muted border-[3px] border-dark border-r-0 text-sm font-body whitespace-nowrap">
          {baseUrl}
        </span>
        
        {/* Editable Slug Input */}
        <BrutalInput
          id={id}
          value={value}
          onChange={handleChange}
          placeholder={placeholder}
          className="flex-1 border-l-0 border-r-0 rounded-none min-w-0"
        />
        
        {/* Copy Button */}
        <BrutalButton
          type="button"
          variant="primary"
          size="sm"
          onClick={handleCopy}
          className="border-l-0 rounded-l-none px-3 py-0 h-auto"
          title={t('components.copyableUrl.copyButton')}
        >
          {isCopied ? (
            <Check className="h-5 w-5" />
          ) : (
            <Copy className="h-5 w-5" />
          )}
          <span className="sr-only">
            {isCopied ? t('components.copyableUrl.copiedShort') : t('components.copyableUrl.copyButton')}
          </span>
        </BrutalButton>
      </div>
    </div>
  );
};
